import type RpcDataPagination  from '@/core/services/Rpc/RpcDataPagination';
import type RpcDataSort        from '@/core/services/Rpc/RpcDataSort';

import Core                    from '@/core/Core';
import ShipmentsToBuyerDAO     from '@/domains/ShipmentsToBuyer/ShipmentToBuyerDAO';
import ShipmentToBuyer         from '@/domains/ShipmentsToBuyer/ShipmentToBuyer';
import ContextMenuItem from "@/lib/components/VContextMenu/ContextMenuItem";
import Shipment from "@/domains/Shipments/Shipment";
import ReceiptsService from "@/domains/Receipts/ReceiptsService";
import Multiplier from "@/core/types/Multiplier";

class ShipmentsToBuyerService {

  /**
   * Загрузить одну реализацию покупателю по ID
   */
  static async get(id: number): Promise<ShipmentToBuyer | null> {
    const response = await ShipmentsToBuyerDAO.get(id);

    return Core.utils.hydrate(response.data, ShipmentToBuyer.fromServer);
  }

  /**
   * Загрузить список реализаций покупателю с пагинацией.
   * Используется в реестре через DataGridLoader.
   *
   * Бек (ShipmentsToBuyerDAO::getShipments) реально поддерживает пагинацию
   * и дефолтную сортировку по id — лоадер подключать без withoutPagination().
   */
  static async getListPaginated(
    params?: Parameters<typeof ShipmentsToBuyerDAO.getList>[0] & {
      pagination?: RpcDataPagination;
      sort?: RpcDataSort;
    }
  ): Promise<{ data: ShipmentToBuyer[]; pagination: RpcDataPagination }> {
    const { pagination, sort, ...filters } = params ?? {};
    const response = await ShipmentsToBuyerDAO.getList(filters, pagination, sort);

    return {
      data:       Core.utils.hydrateArray(response.data, ShipmentToBuyer.fromServer),
      pagination: response.meta.pagination,
    };
  }

  /**
   * Загрузить список реализаций покупателю.
   * Для использования вне реестра (диалоги, автокомплиты).
   */
  static async getList(
    params?: Parameters<typeof ShipmentsToBuyerDAO.getList>[0]
  ): Promise<ShipmentToBuyer[]> {
    return (await this.getListPaginated(params)).data;
  }

  /**
   * Расходится ли фактическая ставка НДС документа с ожидаемой.
   *
   * Правило внешнее по отношению к документу: ожидаемая ставка живёт в
   * конфиге, поэтому сравнение делает сервис, а не модель. Модель отдаёт
   * только vatRateActual — величину, выведенную из собственных сумм.
   *
   * В конфиге ставка лежит процентом, vatRateActual — множитель, поэтому
   * процент сначала приводится к множителю.
   *
   * Сравнение точное. На копеечных суммах округление НДС до копейки способно
   * сдвинуть отношение и дать ложное срабатывание — на реальных суммах
   * документа этого не происходит.
   */
  static isVatRateUnexpected(shipmentToBuyer: ShipmentToBuyer): boolean {
    const expected = Multiplier.fromPercent(Core.config.localDefaultVatRate);

    return shipmentToBuyer.vatRateActual.equals(expected) === false;
  }

  static get contextMenu(): ContextMenuItem[] {
    return [
      new ContextMenuItem(
        'Открыть',
        'open_in_new',
        (shipmentToBuyer: ShipmentToBuyer) => { return this.showShipmentToBuyerCard(shipmentToBuyer.id); },
      )
    ];
  }

  static showShipmentToBuyerCard(id: number): Promise<boolean> {
    return Core.dialogs.ShipmentToBuyerCard.showAsPromise({ id });
  }
}

export default ShipmentsToBuyerService;
